import axios from "axios";
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import type {
  ApiErrorResponse,
  HttpClient,
  HttpRequestConfig,
  HttpResponse,
  HttpClientConfig,
} from "../types";
import { useAuthStore, getTokenFromCookie } from "../../../store/auth";
import * as Sentry from "@sentry/react";

export class AxiosHttpClient implements HttpClient {
  private static instance: AxiosHttpClient | null = null;
  private axiosInstance: AxiosInstance;

  private constructor(config: HttpClientConfig) {
    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...config.headers,
      },
    });

    // Initialize interceptors during construction
    this.initializeInterceptors();
  }

  // Singleton getInstance method
  public static getInstance(config: HttpClientConfig): AxiosHttpClient {
    if (!AxiosHttpClient.instance) {
      AxiosHttpClient.instance = new AxiosHttpClient(config);
    }
    return AxiosHttpClient.instance;
  }

  private transformConfig(config?: HttpRequestConfig): AxiosRequestConfig {
    return {
      headers: config?.headers,
      params: config?.params,
      timeout: config?.timeout,
      responseType: config?.responseType,
      signal: config?.signal,
    };
  }

  private transformResponse<T>(response: AxiosResponse<T>): HttpResponse<T> {
    return {
      data: response.data,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers as Record<string, string>,
    };
  }

  async get<T>(
    url: string,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const response = await this.axiosInstance.get<T>(
      url,
      this.transformConfig(config)
    );
    return this.transformResponse(response);
  }

  async post<T>(
    url: string,
    data?: any,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const response = await this.axiosInstance.post<T>(
      url,
      data,
      this.transformConfig(config)
    );
    return this.transformResponse(response);
  }

  async put<T>(
    url: string,
    data?: any,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const response = await this.axiosInstance.put<T>(
      url,
      data,
      this.transformConfig(config)
    );
    return this.transformResponse(response);
  }

  async delete<T>(
    url: string,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const response = await this.axiosInstance.delete<T>(
      url,
      this.transformConfig(config)
    );
    return this.transformResponse(response);
  }

  async patch<T>(
    url: string,
    data?: any,
    config?: HttpRequestConfig
  ): Promise<HttpResponse<T>> {
    const response = await this.axiosInstance.patch<T>(
      url,
      data,
      this.transformConfig(config)
    );
    return this.transformResponse(response);
  }

  // Private method to initialize both request and response interceptors
  private initializeInterceptors() {
    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }
  private buildFullUrl(config: AxiosRequestConfig): string {
    const cleanBaseURL = (config.baseURL || "").replace(/\/+$/, "");
    const cleanUrlPath = (config.url || "").replace(/^\/+/, "");

    const queryString = config.params
      ? "?" +
        new URLSearchParams(config.params as Record<string, string>).toString()
      : "";

    return `${cleanBaseURL}/${cleanUrlPath}${queryString}`;
  }

  // Private method to setup request interceptor
  private setupRequestInterceptor() {
    this.axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const authState = useAuthStore.getState();
        const token = authState.token ?? getTokenFromCookie();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
    );
  }

  // Private method to setup response interceptor
  private setupResponseInterceptor() {
    this.axiosInstance.interceptors.response.use(
      (response: any) => response,
      async (error: AxiosError) => {
        const status = error?.response?.status;
        const exception = error?.response?.data as ApiErrorResponse | undefined;
        const apiError = exception?.error;
        const errors = apiError?.details;
        const { logout } = useAuthStore.getState();
        const fullUrl = error.config
          ? this.buildFullUrl(error.config)
          : "unknown";
        const method = error.config?.method?.toUpperCase() ?? "UNKNOWN";

        console.error("[Axios] Error response:", {
          message: apiError?.message,
          code: apiError?.code,
          request_id: apiError?.request_id,
          details: errors,
          status,
          fullUrl,
        });
        if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
          console.warn(
            "[Axios] Request was cancelled, skipping error handling"
          );
          return Promise.reject(error);
        }
        if (!error.response && status == null) {
          console.warn(
            "[Axios] No response from server (network or app close), ignoring toast"
          );
          return Promise.reject(error);
        }

        // Report to Sentry for any error that reached here (we have a response; status may be 0, 4xx, 5xx)
        Sentry.withScope((scope) => {
          scope.setTag("layer", "axios");
          scope.setTag("http.method", method);
          scope.setTag("http.status_code", String(status));
          scope.setTag("api_error_code", apiError?.code ?? "unknown");
          scope.setTag("http.url", fullUrl);
          scope.setContext("http_request", {
            url: fullUrl,
            method,
            full_url: fullUrl,
          });

          // Attach full API error so the backend message is visible in Sentry
          scope.setContext("api_error", {
            message: apiError?.message,
            code: apiError?.code,
            request_id: apiError?.request_id,
            details: errors,
          });
          if (apiError?.message) {
            scope.setExtra("api_message", apiError.message);
          }

          Sentry.captureException(error);
        });

        if (status != null && status >= 401 && status <= 499) {
          if (status === 401 || status === 403) {
            console.error("[Axios] Unauthorized or forbidden, logging out");
            logout();
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Get the underlying axios instance if needed
  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}
